import { useState, useEffect, useReducer, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";

import "./OrderConsolidation.scss";
import { convertOrderDraft, getOrderDraft, getQuickRequestDetails, postConsolidatedOrders, updateOrderDraft } from "api";
import Button from "components/v2/Button/Button";
import errorToast from "helpers/errorToast";
import { initialState, reducer } from "pages/shipper/RequestForm/RequestFormState";
import { FormStepTypes } from "constants/";
import Header from "./components/Header/Header";
import OrderSelection from "./components/OrderSelection/OrderSelection";
import ShipmentDetails from "./components/ShipmentDetails/ShipmentDetails";
import ProviderDetails from "./components/ProviderDetails/ProviderDetails";
import Review from "./components/Review/Review";

interface IOrderConsolidationProps {
  isConsolidateQuoteEditRequest?: boolean;
}

function OrderConsolidation(props: IOrderConsolidationProps) {
  const { isConsolidateQuoteEditRequest: isEdit } = props;
  const navigate = useNavigate();
  const methods = useForm<any>({
    mode: "onChange",
    defaultValues: {
      invited_modes: {},
    },
  });

  const {
    getValues,
    setValue,
    watch,
    trigger,
    reset,
    handleSubmit,
    formState: { errors },
  } = methods;

  const [searchParams, setSearchParams] = useSearchParams();
  const [lastAutoSaveDate, setLastAutoSaveDate] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const location = watch("organizational.location");
  const [state, dispatch] = useReducer(reducer, initialState);
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const topRef = useRef<HTMLDivElement>(null);
  const [selectedOrders, setSelectedOrders] = useState<any>([]);
  const [draftId, setDraftId] = useState<string>(searchParams.get("draft_id") || "");
  const organisationFormRef = useRef<HTMLDivElement>(null);
  const scheduleFormRef = useRef<HTMLDivElement>(null);
  const formStepRefs = {
    [FormStepTypes.ORGANISATION]: useRef<any>(null),
    [FormStepTypes.PROVIDER]: useRef<any>(null),
  };
  const steps = ["order-selection", "shipment-details", "provider-details", "review"];

  // Get the current step from search params or fallback to the first step
  const [currentStep, setCurrentStep] = useState<number>(
    steps.indexOf(searchParams.get("active_step") || "order-selection") + 1,
  );
  const [removedOrders, setRemovedOrders] = useState<string[]>([]);
  const [changedOrders, setChangedOrders] = useState<string[]>([]);

  // update the search param when the step changes
  useEffect(() => {
    const activeStep = steps[currentStep - 1]; // Get the step name based on the current step number
    searchParams.set("active_step", activeStep);
    setSearchParams(searchParams);
  }, [currentStep]);

  /**
   * Updates "draft_id" in URL based on the current step.
   * Adds "draft_id" on step 2, removes it on step 1.
   */
  useEffect(() => {
    if (currentStep === 2 && !searchParams.get("draft_id") && draftId) {
      searchParams.set("draft_id", draftId);
    } else if (currentStep === 1 && !isEdit) {
      searchParams.delete("draft_id");
      setDraftId("");
    }
    setSearchParams(searchParams);
  }, [draftId, currentStep]);

  // Sends a request to consolidate selected orders and updates draft ID on success.
  const startConsolidation = () => {
    const available_quick_requests = selectedOrders?.map((order: { reference_number: string }) => order.reference_number);
    const removed_quick_requests = selectedOrders
      ?.map((order: { removed_quick_requests: string | null }) => order?.removed_quick_requests)
      .filter((order: string) => order != null); // Remove null values;
    const payload = {
      available_quick_requests,
      ...(removed_quick_requests.length > 0 && { removed_quick_requests }),
    };
    postConsolidatedOrders(payload)
      .then((res: any) => {
        const { draft_reference } = res?.data;
        setDraftId(draft_reference);
      })
      .catch((error) => {
        errorToast(error);
      });
  };

  /**
   * Fetches draft data and quick request details when `draftId` changes.
   * Updates form values and sets loading state accordingly.
   */
  const fetchDraftAndDetails = async () => {
    setIsLoading(true);
    try {
      // Fetch draft data
      const res = await getOrderDraft(draftId);
      const { available_quick_requests, organizational, packing_list, schedule, service }: any = res?.data;

      // Ensure packing_list is wrapped in an array
      const formattedDraftData = {
        ...res?.data,
        /**
         * Hardcoding address type and good confirmation type since
         * there is no support for seaport and airport in order consolidation
         */
        organizational: { ...organizational, goods_confirmation_type: "Estimated" },
        schedule: { ...schedule, pickup_address_type: "address", stop1_address_type: "address" },
        packing_list: Array.isArray(packing_list) ? packing_list : [packing_list],
        service: getValues("service") ?? service,
      };

      // Reset form values with formatted draft data
      reset(formattedDraftData);

      await fetchOrderDetails(available_quick_requests);
    } catch (err) {
      errorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  async function fetchOrderDetails(orders: any) {
    if (orders?.length === 0) return;
    const allPromiseData = await Promise.all(
      orders?.map(async (id: string) => {
        const data = await getQuickRequestDetails(id);
        return data;
      }),
    );
    setData(allPromiseData);
  }

  useEffect(() => {
    if (draftId) fetchDraftAndDetails();
  }, [draftId]);

  useEffect(() => {
    if (!location || isLoading) return;
    saveDraft();
  }, [location, isLoading]);

  /**
   * Validates the form based on the current step.
   * Scrolls to the error section if validation fails.
   */
  const stepValidation = async (step: number) => {
    let isValid = true;
    switch (step) {
      case 2: {
        // trigger validation for the relevant fields
        isValid = await trigger(["organizational", "schedule", "shipment.title"]);

        // Validate freight mode
        const isFreightModeValid = formStepRefs[FormStepTypes.ORGANISATION].current.checkStepValidation();

        isValid = isValid && isFreightModeValid;
        if (!isValid) {
          if (errors.organizational || errors.invited_modes || errors.shipment) {
            organisationFormRef.current?.scrollIntoView({ behavior: "smooth" });
          } else if (errors.schedule) {
            scheduleFormRef.current?.scrollIntoView({ behavior: "smooth" });
          }
        }
        break;
      }
      case 3: {
        isValid = await trigger(["service", "shipment", "invited_providers", "bucket_attachments"]);
        const isProvidersValid = formStepRefs[FormStepTypes.PROVIDER]?.current?.checkStepValidation(); // Check providers
        isValid = isValid && isProvidersValid;
        break;
      }
      default:
        break;
    }
    return isValid;
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const draftRes = await saveDraft();
      const { removed_quick_requests, changed_packing_list_orders } = draftRes?.data || {};
      if (removed_quick_requests?.length || changed_packing_list_orders?.length) {
        return;
      }
      const res = await convertOrderDraft(draftId);
      const path = `/shipment/${res.data.shipment_id}`;
      navigate(path, { state: { showSuccessPopup: !isEdit } });
    } catch (err) {
      errorToast(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // save draft functionality
  const saveDraft = () => {
    if (!draftId || !location) return Promise.resolve();

    setLastAutoSaveDate(new Date());
    return updateOrderDraft(getValues(), draftId).then((res) => {
      const { available_quick_requests, removed_quick_requests, changed_packing_list_orders, other_attachments }: any =
        res?.data;

      if (other_attachments?.length) setValue("other_attachments", other_attachments);

      // Update removed and changed orders if necessary
      if (removed_quick_requests?.length) {
        setRemovedOrders(removed_quick_requests);
        setCurrentStep(2);
        fetchOrderDetails(available_quick_requests);
      }

      if (changed_packing_list_orders?.length) {
        setChangedOrders(changed_packing_list_orders);
        setCurrentStep(2);
        fetchOrderDetails(available_quick_requests);
      }
      setValue("available_quick_requests", available_quick_requests);
      return res;
    });
  };

  const onBackClick = () => {
    // If the user is on the first step, navigate to the order type selection page
    if (currentStep === 1) navigate("/");

    // Decrement current step and save draft
    saveDraft();
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    topRef.current?.scrollIntoView();
  };

  // Advances to the next step or submits the form if on the last step
  const onNextClick = async () => {
    const isValid = await stepValidation(currentStep);

    if (!isValid) return;

    if (currentStep === 1 && !draftId) startConsolidation();
    if (currentStep === 1 && draftId) {
      const newOrders = selectedOrders.map((order: any) => order.reference_number);
      const currentOrders = getValues("available_quick_requests");
      // Check if any orders have been removed or added
      const isSameOrders =
        newOrders.length === currentOrders.length && newOrders.every((order: string) => currentOrders.includes(order));
      if (isSameOrders) {
        setCurrentStep(2);
        return;
      }
      fetchOrderDetails(newOrders);
      setValue("available_quick_requests", newOrders);
    }

    if (currentStep === 4) {
      await onSubmit();
    } else {
      saveDraft();
      topRef.current?.scrollIntoView();
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div ref={topRef}>
          <Header currentStep={currentStep} lastAutoSaveDate={lastAutoSaveDate} />
          <div className="d-flex flex-column align-items-center" style={{ paddingTop: "48px" }}>
            <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div
                style={{
                  display: currentStep !== 1 ? "none" : "block",
                  minWidth: "800px",
                  maxWidth: "1260px",
                  width: "100%",
                }}
              >
                <OrderSelection
                  selectedOrders={selectedOrders}
                  setSelectedOrders={setSelectedOrders}
                  onNextClick={onNextClick}
                  isEdit={isEdit}
                />
              </div>
              <div style={{ minWidth: "800px" }}>
                <div style={{ display: currentStep !== 2 ? "none" : "" }}>
                  <ShipmentDetails
                    data={data}
                    state={state}
                    dispatch={dispatch}
                    setCurrentStep={setCurrentStep}
                    isLoading={isLoading}
                    refs={{
                      organisationFormRef,
                      scheduleFormRef,
                    }}
                    childRef={formStepRefs[FormStepTypes.ORGANISATION]}
                    removedOrders={removedOrders}
                    setRemovedOrders={setRemovedOrders}
                    changedOrders={changedOrders}
                    setChangedOrders={setChangedOrders}
                  />
                </div>
                <div style={{ display: currentStep !== 3 ? "none" : "" }}>
                  <ProviderDetails state={state} dispatch={dispatch} childRef={formStepRefs[FormStepTypes.PROVIDER]} />
                </div>
                <div style={{ display: currentStep !== 4 ? "none" : "" }}>
                  <Review data={data} isLoading={isLoading} state={state} />
                </div>
                {/* For step 1 a different footer will be rendered from OrderSelection.tsx */}
                {currentStep !== 1 && (
                  <div className="d-flex justify-content-end w-100 mt-3xl">
                    <div className="step-buttons">
                      <Button label="Back" variant="link-gray" size="md" onClick={onBackClick} disabled={isSubmitting} />
                      <Button
                        label={currentStep === 4 ? "Submit" : currentStep === 3 ? "Review" : "Next"}
                        variant="primary"
                        size="md"
                        onClick={onNextClick}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

OrderConsolidation.defaultProps = {
  isConsolidateQuoteEditRequest: false,
};

export default OrderConsolidation;
